import { useMutation, useQueryClient } from "@tanstack/react-query";
import imageCompression from "browser-image-compression";
import jsPDF from "jspdf";
import {
  ChangeEvent,
  DetailedHTMLProps,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import AudioCaptureDialog from "@/components/Files/AudioCaptureDialog";
import CameraCaptureDialog from "@/components/Files/CameraCaptureDialog";

import mutate from "@/Utils/request/mutate";
import uploadFile from "@/Utils/request/uploadFile";
import {
  DEFAULT_ALLOWED_EXTENSIONS,
  FILE_EXTENSIONS,
  FileCategory,
  FileRead,
  FileReadMinimal,
  FileType,
} from "@/types/files/file";
import fileApi from "@/types/files/fileApi";

export type FileUploadOptions = {
  multiple?: boolean;
  type: FileType;
  category?: FileCategory;
  onUpload?: (file: FileReadMinimal) => void;
  // if allowed, will fallback to the name of the file if a seperate filename is not defined.
  allowNameFallback?: boolean;
  compress?: boolean;
  locked?: boolean;
} & (
  | {
      allowedExtensions?: string[];
    }
  | {
      allowAllExtensions?: boolean;
    }
);

export type FileInputProps = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "id" | "title" | "type" | "accept" | "onChange"
> & {};

export type FileUploadReturn = {
  progress: null | number;
  error: null | string;
  setError: (error: string | null) => void;
  validateFiles: () => boolean;
  handleCameraCapture: () => void;
  handleAudioCapture: () => void;
  handleFileUpload: (
    associating_id: string,
    combineToPDF?: boolean,
  ) => Promise<void>;
  Dialogues: React.ReactNode;
  Input: (_: FileInputProps) => React.ReactNode;
  fileNames: string[];
  files: File[];
  setFileName: (names: string, index?: number) => void;
  setFileNames: (names: string[]) => void;
  removeFile: (index: number) => void;
  clearFiles: () => void;
  pendingFiles: string[];
  removePendingFile: (index: number) => void;
  clearPendingFiles: () => void;
  stageFiles: (combineToPDF?: boolean) => Promise<boolean>;
  uploadPendingFiles: (associating_id: string) => Promise<boolean>;
  uploading: boolean;
  previewing?: boolean;
};

export default function useFileUpload(
  options: FileUploadOptions,
): FileUploadReturn {
  const {
    type: fileType,
    onUpload,
    category = FileCategory.UNSPECIFIED,
    multiple,
    allowNameFallback = true,
    locked = false,
  } = options;

  const { t } = useTranslation();
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  const [uploadFileNames, setUploadFileNames] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<null | number>(null);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [audioModalOpen, setAudioModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [files, setFiles] = useState<File[]>([]);
  const [pendingUploads, setPendingUploads] = useState<
    { file: File; name: string }[]
  >([]);
  const queryClient = useQueryClient();

  const generatePDF = async (files: File[]): Promise<File | null> => {
    try {
      toast.info(t("file_conversion_in_progress"));
      const pdf = new jsPDF();
      const totalFiles = files.length;

      for (const [index, file] of files.entries()) {
        const imgData = URL.createObjectURL(file);
        pdf.addImage(imgData, "JPEG", 10, 10, 190, 0);
        URL.revokeObjectURL(imgData);
        if (index < files.length - 1) pdf.addPage();
        const progress = Math.round(((index + 1) / totalFiles) * 100);
        setProgress(progress);
      }
      const pdfBlob = pdf.output("blob");
      const pdfFile = new File([pdfBlob], "combined.pdf", {
        type: "application/pdf",
      });
      setProgress(0);
      toast.success(t("file_conversion_success"));
      return pdfFile;
    } catch (error) {
      toast.error(t("file_error__generate_pdf"));
      setError(t("file_error__generate_pdf", { error: String(error) }));
      setProgress(0);
      return null;
    }
  };
  const onFileChange = (e: ChangeEvent<HTMLInputElement>): any => {
    if (lockedRef.current) return;
    if (!e.target.files?.length) {
      return;
    }
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
    if (options.compress) {
      selectedFiles.forEach((file) => {
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (ext && (FILE_EXTENSIONS.IMAGE as readonly string[]).includes(ext)) {
          const options = {
            initialQuality: 0.6,
            alwaysKeepResolution: true,
          };
          imageCompression(file, options).then((compressedFile: File) => {
            setFiles((prev) =>
              prev.map((f) => (f.name === file.name ? compressedFile : f)),
            );
          });
        }
      });
    }
  };

  useEffect(() => {
    const blanks = Array(files.length).fill("");
    setUploadFileNames((names) => [...names, ...blanks].slice(0, files.length));
  }, [files]);

  const validateFileUpload = () => {
    if (files.length === 0) {
      setError(t("file_error__choose_file"));
      return false;
    }

    for (const file of files) {
      const filenameLength = file.name.trim().length;
      if (filenameLength === 0) {
        setError(t("file_error__file_name"));
        return false;
      }
      if (file.size > 10e7) {
        setError(t("file_error__file_size"));
        return false;
      }
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (
        "allowedExtensions" in options &&
        !options.allowedExtensions
          ?.map((extension) => extension.replace(".", "").toLowerCase())
          ?.includes(extension || "")
      ) {
        setError(
          t("file_error__file_type", {
            extension,
            allowedExtensions: options.allowedExtensions?.join(", "),
          }),
        );
        return false;
      }
    }
    return true;
  };
  const { mutateAsync: markUploadComplete } = useMutation({
    mutationFn: (fileId: string) =>
      mutate(fileApi.markUploadCompleted, {
        pathParams: { fileId },
      })(undefined),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["files", fileType, data.associating_id],
      });
      toast.success(t("file_uploaded"));
      setError(null);
      onUpload?.(data);
    },
  });

  const uploadfile = async (data: FileRead, file: File) => {
    const url = data.signed_url;
    const internal_name = data.internal_name;
    const newFile = new File([file], `${internal_name}`);

    return new Promise<void>((resolve, reject) => {
      uploadFile(
        url,
        newFile,
        "PUT",
        { "Content-Type": file.type },
        async (xhr: XMLHttpRequest) => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setProgress(null);
            try {
              await markUploadComplete(data.id);
              resolve();
            } catch (error) {
              toast.error(t("file_error__mark_complete_failed"));
              reject(error);
            }
          } else {
            toast.error(
              t("file_error__dynamic", { statusText: xhr.statusText }),
            );
            setProgress(null);
            reject();
          }
        },
        setProgress as any,
        () => {
          toast.error(t("file_error__network"));
          setProgress(null);
          reject();
        },
      ).catch(reject);
    });
  };

  const { mutateAsync: createUpload } = useMutation({
    mutationFn: mutate(fileApi.create),
  });

  const clearFiles = () => {
    setFiles([]);
    setError(null);
    setUploadFileNames([]);
  };

  const uploadFiles = async (
    associating_id: string,
    filesToUpload: File[],
    namesToUpload: string[],
  ) => {
    setUploading(true);
    const failedUploads: { file: File; name: string }[] = [];

    for (const [index, file] of filesToUpload.entries()) {
      const name = namesToUpload[index] || file.name;
      try {
        const data = await createUpload({
          original_name: file.name ?? "",
          file_type: fileType as FileType,
          name,
          associating_id,
          file_category: category,
          mime_type: file.type ?? "",
        });

        if (data) {
          await uploadfile(data, file);
        }
      } catch {
        failedUploads.push({ file, name });
      }
    }

    setUploading(false);
    if (failedUploads.length > 0) {
      setError(t("file_error__network"));
    } else {
      setError(null);
    }
    return failedUploads;
  };

  const validateUploadNames = (names: string[], combineToPDF?: boolean) => {
    if (combineToPDF) {
      if (!names.length || !names[0]?.trim()) {
        setError(t("file_error__single_file_name"));
        return false;
      }
    } else if (names.some((name) => !name.trim())) {
      setError(t("file_error__single_file_name"));
      return false;
    }
    return true;
  };

  const stageFiles = async (combineToPDF = false) => {
    if (lockedRef.current) return false;
    if (!validateFileUpload()) return false;

    let filesToStage = [...files];
    let namesToStage = [...uploadFileNames];
    if (combineToPDF && filesToStage.length > 1) {
      const pdfFile = await generatePDF(filesToStage);
      if (!pdfFile) return false;
      filesToStage = [pdfFile];
      namesToStage = [namesToStage[0]];
    }

    if (!validateUploadNames(namesToStage, combineToPDF)) return false;
    if (lockedRef.current) return false;

    setPendingUploads((previous) => [
      ...previous,
      ...filesToStage.map((file, index) => ({
        file,
        name: namesToStage[index] || file.name,
      })),
    ]);
    clearFiles();
    toast.success("Files attached. Save results to upload them.");
    return true;
  };

  const uploadPendingFiles = async (associating_id: string) => {
    if (pendingUploads.length === 0) return true;
    const failedUploads = await uploadFiles(
      associating_id,
      pendingUploads.map(({ file }) => file),
      pendingUploads.map(({ name }) => name),
    );
    setPendingUploads(failedUploads);
    return failedUploads.length === 0;
  };

  const handleUpload = async (
    associating_id: string,
    combineToPDF?: boolean,
  ) => {
    if (lockedRef.current) return;
    if (combineToPDF && "allowedExtensions" in options) {
      options.allowedExtensions = ["jpg", "png", "jpeg"];
    }
    if (!validateFileUpload()) return;

    setProgress(0);
    const namesToUpload = uploadFileNames.map((name, index) =>
      allowNameFallback && !name && files[index] ? files[index].name : name,
    );
    if (combineToPDF) {
      if (!namesToUpload.length || !namesToUpload[0]) {
        setError(t("file_error__single_file_name"));
        return;
      }
    } else {
      for (const filename of namesToUpload) {
        if (!filename.trim()) {
          setError(t("file_error__single_file_name"));
          return;
        }
      }
    }

    let filesToUpload = files;
    if (combineToPDF && files.length > 1) {
      const pdfFile = await generatePDF(files);
      if (pdfFile) {
        filesToUpload = [pdfFile];
      } else {
        clearFiles();
        setError(t("file_error__generate_pdf"));
        return;
      }
    }

    const failedUploads = await uploadFiles(
      associating_id,
      filesToUpload,
      namesToUpload,
    );
    setFiles(failedUploads.map(({ file }) => file));
    setUploadFileNames(failedUploads.map(({ name }) => name));
    setCameraModalOpen(false);
  };

  const Dialogues = (
    <>
      <CameraCaptureDialog
        open={cameraModalOpen}
        onOpenChange={(open) => setCameraModalOpen(open)}
        onCapture={(file) => {
          setFiles((prev) => [...prev, file]);
        }}
        onResetCapture={clearFiles}
        setPreview={setPreviewing}
      />
      <AudioCaptureDialog
        show={audioModalOpen}
        onHide={() => setAudioModalOpen(false)}
        onCapture={(file) => {
          setFiles((prev) => [...prev, file]);
        }}
        autoRecord
      />
    </>
  );

  const Input = (props: FileInputProps) => (
    <input
      {...props}
      id={`file_upload_${fileType}`}
      title={t("change_file")}
      onChange={onFileChange}
      type="file"
      multiple={multiple}
      accept={
        "allowedExtensions" in options
          ? options.allowedExtensions?.map((e) => "." + e).join(",")
          : "allowAllExtensions" in options
            ? "*"
            : DEFAULT_ALLOWED_EXTENSIONS.join(",")
      }
      hidden={props.hidden || true}
    />
  );

  return {
    progress,
    error,
    setError,
    validateFiles: validateFileUpload,
    handleCameraCapture: () => setCameraModalOpen(true),
    handleAudioCapture: () => setAudioModalOpen(true),
    handleFileUpload: handleUpload,
    Dialogues,
    Input,
    fileNames: uploadFileNames,
    files: files,
    setFileNames: setUploadFileNames,
    setFileName: (name: string, index = 0) => {
      setUploadFileNames((prev) =>
        prev.map((n, i) => (i === index ? name : n)),
      );
    },
    removeFile: (index = 0) => {
      setFiles((prev) => prev.filter((_, i) => i !== index));
      setUploadFileNames((prev) => prev.filter((_, i) => i !== index));
    },
    clearFiles,
    pendingFiles: pendingUploads.map(({ name }) => name),
    removePendingFile: (index) => {
      if (lockedRef.current) return;
      setPendingUploads((prev) => prev.filter((_, i) => i !== index));
    },
    clearPendingFiles: () => {
      if (lockedRef.current) return;
      setPendingUploads([]);
    },
    stageFiles,
    uploadPendingFiles,
    uploading,
    previewing,
  };
}
